// OptiClock — support-agent time tracking. Mirrors the staff-api Opticlock* GraphQL shapes
// (apps/api/src/app/opticlock/opticlock.response.ts). Vendor by copy.
//
// `entryDate` is a STRING (`YYYY-MM-DD`), never a Date. It is a bare calendar date a human
// picked; parsing it into a Date is what reintroduces the month-boundary drift the whole
// feature is built to avoid. `createdAt` / `updatedAt` are real instants and stay ISO strings
// on the wire.

/** The closed colour token set a project may carry. Mirrors OPTICLOCK_COLORS on the api. */
export const OPTICLOCK_COLORS = [
  'slate',
  'red',
  'amber',
  'green',
  'teal',
  'blue',
  'violet',
  'pink',
] as const;

export type OpticlockColor = (typeof OPTICLOCK_COLORS)[number];

/** Smallest bookable unit, in minutes. The picker and a CHECK constraint both enforce it. */
export const OPTICLOCK_MINUTE_STEP = 15;

/** One agent's ceiling for a single calendar day (24:00), enforced service-side. */
export const OPTICLOCK_MAX_DAY_MINUTES = 1440;

export interface OpticlockProject {
  /** The immutable slug, e.g. 'rma-fulfillment'. Also the external API's `projectId`. */
  _id: string;
  name: string;
  color?: string | null;
  /** Archived projects leave the picker but their entries keep counting. */
  archived: boolean;
  sortOrder: number;
  createdBy: string;
  createdAt?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

export interface OpticlockEntry {
  _id: string;
  /** The OWNER. Never rewritten — a root edit records itself in `updatedBy`. */
  agentEmail: string;
  projectId: string;
  projectName?: string | null;
  projectColor?: string | null;
  /** `YYYY-MM-DD` — a bare calendar date. Do not parse into a Date. */
  entryDate: string;
  minutes: number;
  note?: string | null;
  createdAt?: string | null;
  createdBy: string;
  updatedAt?: string | null;
  /** Set ONLY when root edited an entry it does not own. */
  updatedBy?: string | null;
}

/** One agent's month — rows and total together, so the footer cannot disagree with the list. */
export interface OpticlockMyMonth {
  month: string;
  agentEmail: string;
  totalMinutes: number;
  /** `H:MM`, formatted server-side by the one formatter. */
  totalHours: string;
  entries: OpticlockEntry[];
}

export interface OpticlockProjectSplit {
  projectId: string;
  name: string;
  minutes: number;
  color?: string | null;
}

export interface OpticlockAgentMonth {
  agentEmail: string;
  minutes: number;
  hours: string;
  entryCount: number;
  latestProjectId?: string | null;
  latestProjectName?: string | null;
  /** `YYYY-MM-DD`. */
  latestEntryDate?: string | null;
  latestCreatedAt?: string | null;
  /**
   * Ordered minutes-DESC by the server. Render in array order — that ordering is what keeps
   * the stacked bar's colours stable across refetches.
   */
  byProject: OpticlockProjectSplit[];
}

export interface OpticlockEntryInput {
  projectId: string;
  entryDate: string;
  minutes: number;
  note?: string;
}

export interface OpticlockEntryPatchInput {
  projectId?: string;
  entryDate?: string;
  minutes?: number;
  note?: string;
}

export interface OpticlockProjectInput {
  /** Omit to create (slug derived from name); pass to update. The slug is immutable. */
  id?: string;
  name: string;
  color?: string;
  sortOrder?: number;
}
